import { computePosition, autoUpdate, ReferenceElement, AutoUpdateOptions, ComputePositionConfig, flip, shift, limitShift, offset, Placement } from '@floating-ui/dom';


export type PopperPlacement = Placement;

/** 
 * Mirrors the options from floating-ui, in a way that does not require the calling code to reference the library directly.
 * This way, we could theoretically switch to another library in the future, without changing the calling code.
 * This approach also makes it easier to compile the popper dependencies.
 **/
export interface PopperOptions {
    /** Where to place the popper relative to the reference element */
    placement: PopperPlacement;
    /** Gap between the reference element and the popper */
    offset?: number;
}

export function createPopper(referenceEl: ReferenceElement, floatingEl: HTMLElement, options: PopperOptions) {
    // For now, we will always use the flip and shift middleware
    // The crossAxis: true option allows popper to be positioned in front of the reference element when there is not enough space to display it in the desired location.
    const middleware = [
        flip(),
        shift({ limiter: limitShift(), crossAxis: true }),
    ];
    if (options.offset) {
        middleware.push(offset(options.offset));
    }
    return createPopperUsingFloatingUi(referenceEl, floatingEl, { 
        placement: options.placement, 
        middleware 
    });
}

/** Adapted from the floating-ui migration guide, to match the old `createPopper` function: https://floating-ui.com/docs/migration */
function createPopperUsingFloatingUi(referenceEl: ReferenceElement, floatingEl: HTMLElement, placementOptions: Partial<ComputePositionConfig> = {}, autoUpdateOptions?: AutoUpdateOptions) {
    return autoUpdate(referenceEl, floatingEl, () => {
        computePosition(referenceEl, floatingEl, placementOptions).then(({ x, y }) => {
            Object.assign(floatingEl.style, {
                left: `${x}px`,
                top: `${y}px`,
            });
        });
    }, autoUpdateOptions);
}